import { KeyboardEvent } from "react"
import { convertwoz2dsk } from "./convertwoz2dsk"

// Be sure to update this version whenever the save state format changes.
export const SAVE_STATE_VERSION = 2.0

export const TEST_DEBUG = false
export const TEST_GRAPHICS = false
export const MAX_SNAPSHOTS = 30
const FILE_OPEN_SUFFIXES = ".a2ts,.bin,.a,.bas"
const FLOPPY_DISK_SUFFIXES = ".dsk,.woz,.do,.nib"
const HARD_DRIVE_SUFFIXES = ".2mg,.hdv,.po,.2meg,.img"
export const FILE_SUFFIXES_DISK = `${FLOPPY_DISK_SUFFIXES},${HARD_DRIVE_SUFFIXES}`
export const FILE_SUFFIXES_ALL = `${FILE_OPEN_SUFFIXES},${FILE_SUFFIXES_DISK},.zip,.gz,.7z,.tar`
export const MAX_DRIVES = 4
// export const DISK_CONVERSION_SUFFIXES = new Map<string, string>([[".dsk", ".woz"]])

// Put memory offset constants here so we can use them in the worker and main thread.
// We used to have these in the memory.ts file, but when we imported that into
// the main thread, it also created an unnecessary memory buffer array.
export const ROMpage = 0x100
export const RamWorksPage = 0x17F
export const ROMmemoryStart = 256 * ROMpage
export const RamWorksMemoryStart = 256 * RamWorksPage

export enum RUN_MODE {
  IDLE = 0,
  RUNNING = -1,
  PAUSED = -2,
  NEED_BOOT = -3,
  NEED_RESET = -4,
}

export enum MSG_WORKER {
  MACHINE_STATE,
  CLICK,
  DRIVE_PROPS,
  DRIVE_SOUND,
  ERRORMSG,
  GET_MEMORY_RESPONSE,
  SAVE_STATE,
  RUMBLE,
  HELP_TEXT,
  SHOW_APPLE_MOUSE,
  MBOARD_SOUND,
  COMM_DATA,
  MIDI_DATA,
  ENHANCED_MIDI,
  REQUEST_THUMBNAIL,
  SOFTSWITCH_DESCRIPTIONS,
  INSTRUCTIONS,
  SERIAL_CONFIG_CHANGE,
  VERA_FRAME = 18,
  VERA_PCM_WRITE,
  VERA_PSG_WRITE,
  VERA_PSG_BATCH,
  VERA_FM_WRITE,
  VERA_FM_RESET,
  OPERATION_RESULT,
  VERA_SD_STATUS,
  VERA_SD_IMAGE_DATA,
}

export interface VeraSdStatus {
  attached: boolean
  name: string
  size: number
  lba?: number
  writeProtected?: boolean
  hasChanges?: boolean
}

export enum MSG_MAIN {
  APPLE_PRESS,
  APPLE_RELEASE,
  APP_MODE,
  AUTO_SNAPSHOT,
  BASIC_STEP,
  BREAKPOINTS,
  COMM_DATA,
  CYCLES_TO_RUN,
  DEBUG,
  DRIVE_NEW_DATA,
  DRIVE_PROPS,
  ENABLE_GAME_PATCHES,
  EXECUTE_BASIC_COMMAND,
  GAMEPAD,
  GET_MEMORY_REQUEST,
  GET_SAVE_STATE,
  GET_SAVE_STATE_SNAPSHOTS,
  KEYBOARD_STATE,
  KEY_SEQUENCE,
  KEYPRESS,
  KEYRELEASE,
  MACHINE_NAME,
  MEMORY_DUMP_STATE,
  MIDI_DATA,
  MOUSEEVENT,
  PASTE_TEXT,
  RAMWORKS,
  RESTORE_STATE,
  REVERSE_YAXIS,
  RUN_MODE,
  SET_BINARY_BLOCK,
  SET_CYCLECOUNT,
  SET_MEMORY,
  SHOW_DEBUG_TAB,
  SIRIUS_JOYPORT,
  SOFTSWITCHES,
  SPEED,
  STATE6502,
  STEP_INTO,
  STEP_OUT,
  STEP_OVER,
  THUMBNAIL_IMAGE,
  TIME_TRAVEL_INDEX,
  TIME_TRAVEL_SNAPSHOT,
  TIME_TRAVEL_STEP,
  TRACING,
  TRACE_SETTINGS,
  VERA_SLOT,
  VERA_FM_ENABLED,
  VIDEO7_OVERRIDE,
  SLOT_CONFIG,
  RUN_BINARY,
  LOAD_BINARY,
  GET_MEMORY_VIEW,
  WRITE_MEMORY,
  FIND_MEMORY,
  SET_MEMORY_WRITE_WATCHPOINT,
  CLEAR_MEMORY_WRITE_WATCHPOINT,
  CREATE_SESSION_SNAPSHOT,
  RESTORE_SESSION_SNAPSHOT,
  COMPARE_SESSION_MEMORY,
  CONDITIONAL_KEY_SEQUENCE,
  CANCEL_CONDITIONAL_KEY_SEQUENCE,
  VERA_SD_IMAGE,
  VERA_SD_GET_IMAGE,
  VERA_SD_WRITE_PROTECT,
  VERA_SD_CLEAR_CHANGES,
}

export const DEFAULT_SLOT_CONFIG: SlotConfig = {
  1: "ssc",
  2: "none",
  3: "aux",
  4: "mockingboard",
  5: "mouse",
  6: "disk2",
  7: "smartport",
}

export enum AUTO_SNAPSHOT {
  AUTO_OFF,
  AUTO_100K,
  AUTO_1M,
}

export enum COLOR_MODE {
  COLOR,
  NOFRINGE,
  GREEN,
  AMBER,
  BLACKANDWHITE,
  INVERSEBLACKANDWHITE
}

export enum MONITOR_MODE {
  NTSC,
  RGB,
}

export enum ARROW {
  LEFT,
  RIGHT,
  UP,
  DOWN
}

export enum UI_THEME {
  CLASSIC,
  DARK,
  MINIMAL,
}

export enum ERROR_CODE {
  INFO,
  WARNING,
  ERROR,
  QUESTION,
}

export const UI_THEMES = [
  { value: UI_THEME.CLASSIC, name: "Classic", queryValue: "classic" },
  { value: UI_THEME.DARK, name: "Dark", queryValue: "dark" },
  { value: UI_THEME.MINIMAL, name: "Minimal", queryValue: "minimal" },
] as const

export const themeToName = (theme: UI_THEME) => {
  return UI_THEMES.find(option => option.value === theme)?.name ?? UI_THEMES[0].name
}

export type MouseEventSimple = {
  x : number; // 0.0 -> 1.0
  y : number; // 0.0 -> 1.0
  //    -1:  on mouse move
  //  0x00:  button 0 up
  //  0x10:  button 0 down
  //  0x01:  button 1 up
  //  0x11:  button 1 down
  buttons : number;
}

export enum MEMORY_DUMP_STATE {
  CURRENT,
  MAIN,
  AUX,
  HGR1,
  HGR2,
  NONE,
}

export const colorToName = (mode: COLOR_MODE) => {
  return ["Color", "Color (no fringe)", "Green", "Amber", "Black and White", "Black and White (inverse)"][mode]
}

export const nameToColorMode = (name: string) => {
  switch (name) {
    case "Color (no fringe)": return COLOR_MODE.NOFRINGE
    case "Green": return COLOR_MODE.GREEN
    case "Amber": return COLOR_MODE.AMBER
    case "Black and White": return COLOR_MODE.BLACKANDWHITE
    case "Black and White (inverse)": return COLOR_MODE.INVERSEBLACKANDWHITE
    default: return COLOR_MODE.COLOR
  }
}

export enum DRIVE {
  MOTOR_OFF,
  MOTOR_ON,
  TRACK_END,
  TRACK_SEEK,
}

export enum CLOUD_SYNC {
  INACTIVE,
  ACTIVE,
  PENDING,
  INPROGRESS,
  FAILED
}

export enum ADDR_MODE {
  IMPLIED,  // BRK
  IMM,      // LDA #$01
  ZP_REL,   // LDA $C0 or BCC $FF
  ZP_X,     // LDA $C0,X
  ZP_Y,     // LDX $C0,Y
  ABS,      // LDA $1234
  ABS_X,    // LDA $1234,X
  ABS_Y,    // LDA $1234,Y
  IND_X,    // LDA ($FF,X) or JMP ($1234,X)
  IND_Y,    // LDA ($FF),Y
  IND       // JMP ($1234) or LDA ($C0)
}

export const default6502State = (): STATE6502 => {
  return {
    cycleCount: 0,
    PStatus: 0,
    PC: 0,
    prevPC: 0,
    Accum: 0,
    XReg: 0,
    YReg: 0,
    StackPtr: 0,
    flagIRQ: 0,
    flagNMI: false
  }
}

// A hack to determine if this is a relative instruction.
export const isBranchInstruction = (instr: string) => instr.startsWith("B") && instr !== "BIT" && instr !== "BRK"

// export const toBinary = (value: number, ndigits = 8) => {
//   return ("0000000000000000" + value.toString(2)).slice(-ndigits)
// }

export const toHex = (value: number, ndigits = 2) => {
  if (value > 0xFF) {
    ndigits = 4
  }
  return ("0000" + value.toString(16).toUpperCase()).slice(-ndigits)
}

export const convertAppleKey = (e: KeyboardEvent, lowercase: boolean,
  ctrlKeyMode: number, cout: number) => {
  let key = -1
  if (e.altKey && e.key !== "Alt") {
    e.key = String.fromCharCode(e.keyCode)
  }
  if (e.key.length === 1) {
    key = e.key.charCodeAt(0)
    if (e.ctrlKey || ctrlKeyMode > 0) {
      if (key >= 0x40 && key <= 0x7E) {
        key &= 0b00011111
      } else {
        return -1
      }
    } else if (!lowercase) {
      key = String.fromCharCode(key).toUpperCase().charCodeAt(0)
    }
  } else {
    const keymap: Record<string, number> = {
      Enter: 13,
      ArrowRight: 21,
      ArrowLeft: 8,
      Backspace: 0x7F,  // Apple II Delete (rubout) key
      ArrowUp: 11,
      ArrowDown: 10,
      Escape: 27,
      Tab: 9,
      Shift: -1,
      Control: -1
    }
    // The default behavior for the "delete" key on the Apple II is to send
    // a rubout. This is expected by Appleworks, A2osX, and A2DeskTop.
    // However, if we are using the default COUT routine then send a backspace
    // character instead of rubout. This will work better in Applesoft BASIC.
    // Since there may be cases where this is wrong, if the shift key is down
    // then still send the rubout character.
    // If this is still a problem, we may need to add a settings option,
    // but I'm hoping to avoid that.
    if (e.key === "Backspace" && cout === 0xFD1B && !e.shiftKey) {
      key = 8
    } else if (e.key in keymap) {
      key = keymap[e.key]
    }
  }
  return key
}

export const convertTextPageValueToASCII = (
  value: number,
  isAltCharSet: boolean,
  hasMouseText: boolean,
  hasLowerCase = true,
  useApple2PlusMap = false
) => {
  let v1 = value
  const original = value
  if (v1 >= 0 && v1 <= 31) {
    // Shift Ctrl chars into ASCII A-Z range
    // They will be displayed as inverse
    v1 += 64
  } else if (v1 >= 160) {
    // Normal text, just strip the high bit to convert to standard ASCII range
    v1 &= 0b01111111
    // Apple II/II+ text conventions: many punctuation/space characters in
    // the 0xE0-0xFF range map to ASCII by shifting 0x60-0x7F down to 0x20-0x3F.
    // Example: E0 -> 0x60 -> 0x20 (space), E2 -> 0x62 -> 0x22 (quote).
    if (useApple2PlusMap && original >= 0xE0 && v1 >= 0x60) {
      v1 -= 0x40
    }
  } else if (isAltCharSet) {
    if (hasMouseText && v1 >= 64 && v1 <= 95) {
      // Shift Mousetext chars into extended ASCII range.
      // (These are flashing chars in the normal char set.)
      v1 += 64
    } else if (v1 >= 128) {
      // 128...159 need to be shifted down to their ASCII display value.
      v1 -= 64
    }
    // 32...63 are unchanged and will be displayed as inverse.
    // 96...127 are unchanged and will be displayed as inverse.
  } else {
    // 32...95 are displayed as their actual ASCII values but will be either
    // inverse (32-63) or flashing (64-95).
    // 96...127 need to be shifted down to their ASCII display value and
    // will be displayed as flashing.
    // 128...159 also need to be shifted down to their ASCII display value
    // but will be displayed as normal.
    if (v1 >= 96) {
      v1 -= 64
    }
  }
  if (!hasLowerCase && v1 >= 0x61 && v1 <= 0x7A) {
    v1 -= 0x20
  }
  return String.fromCodePoint(v1 >= 127 ? (0xE000 + v1) : v1)
}

const zpPrev = new Uint8Array(256).fill(0)

export const debugZeroPage = (zp: Uint8Array) => {
  let diff = ""
  for (let i = 0; i < 256; i++) {
    if (zp[i] !== zpPrev[i]) {
      diff += " " + toHex(i) + ":" + toHex(zpPrev[i]) + ">" + toHex(zp[i])
      zpPrev[i] = zp[i]
    }
  }
  if (diff !== "") console.log(diff)
}

export const toASCII = (s: string) => s.split("").map(char => char.charCodeAt(0))
export const uint16toBytes = (n: number) => [n & 0xFF, (n >>> 8) & 0xFF]
export const uint32toBytes = (n: number) => [n & 0xFF, (n >>> 8) & 0xFF,
  (n >>> 16) & 0xFF, (n >>> 24) & 0xFF]

export const replaceSuffix = (fname: string, suffix: string) => {
  const i = fname.lastIndexOf(".") + 1
  return fname.substring(0, i) + suffix
}

const crcTable = new Uint32Array(256).fill(0)

const makeCRCTable = () => {
  let c
  for (let n =0; n < 256; n++) {
    c = n
    for (let k =0; k < 8; k++) {
      c = ((c&1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1))
    }
    crcTable[n] = c
  }
}

export const crc32 = (data: Uint8Array, offset = 0) => {
  if (crcTable[255] === 0) {
    makeCRCTable()
  }
  let crc = 0 ^ (-1)
  for (let i = offset; i < data.length; i++) {
    crc = (crc >>> 8) ^ crcTable[(crc ^ data[i]) & 0xFF]
  }

  return (crc ^ (-1)) >>> 0
}

export const isWozData = (data?: Uint8Array | null): boolean => {
  return Boolean(data && data.length >= 4 && data[0] === 0x57 && data[1] === 0x4F && data[2] === 0x5A)
}

export const updateWozCrc = (diskData?: Uint8Array | null): Uint8Array | undefined => {
  if (diskData && isWozData(diskData) && diskData.length >= 12) {
    const crc = crc32(diskData, 12)
    diskData.set(uint32toBytes(crc), 8)
  }
  return diskData ?? undefined
}

export const getBlobFromDiskData = (diskData: Uint8Array, filename: string): Blob => {
  if (filename.toLowerCase().endsWith(".woz") || isWozData(diskData)) {
    updateWozCrc(diskData)
  }
  return new Blob([diskData] as BlobPart[], { type: "application/octet-stream" })
}

export const getDiskBlobForSync = (diskData: Uint8Array, filename: string, isHardDrive = false): Blob => {
  const fileExt = filename.substring(filename.lastIndexOf(".")).toLowerCase()
  const isFloppy = !isHardDrive && [".dsk", ".do", ".po"].includes(fileExt)
  let targetData = diskData
  if (isFloppy) {
    const converted = convertwoz2dsk(diskData, fileExt === ".po")
    if (converted.length > 0) {
      targetData = converted
    }
  }
  return getBlobFromDiskData(targetData, filename)
}

export const lockedKeyStyle = (mode: number) => {
  return `push-button key-button ${(["", "button-active", "button-locked"])[mode]}`
}

export const hiresLineToAddress = (pageOffset: number, line: number) => {
  return pageOffset + 40 * Math.trunc(line / 64) +
    1024 * (line % 8) + 128 * (Math.trunc(line / 8) & 7)
}

export const hiresAddressToLine = (addr: number) => {
  const base = addr & 0x1FFF
  // This give 0, 1, or 2 for the 3 different regions of the screen.
  // (The Math.min clips the extra 8 screen holes at the end of the 3rd line)
  const groupOf3 = Math.trunc((Math.min(base & 0x7F, 0x77)) / 40)
  // This gives the smaller chunks of 8 lines each within the groups of 8.
  const chunkOf8 = Math.trunc((base & 0x3FF) / 128)
  // This gives the individual lines within each chunk of 8, which oddly
  // enough have the largest address spacing of 0x400 between each line.
  const individualLine = Math.trunc(base / 1024)
  const result = 64 * groupOf3 + 8 * chunkOf8 + individualLine
  return result
}

// for (let i = 0; i < 192; i++) {
//   const addr = hiresLineToAddress(0x2000, i)
//   const line = hiresAddressToLine(addr + 1)
//   if (i !== line) console.log("ERROR")
// }


let machineSymbolTable = new Map<number, string>()
let machineName_cached = ""
let fetchingTable = false
let userSymbolTable = new Map<number, string>()


const processSymbolData = (text: string) => {
  const lines = text.split("\n")
  const symbolMap = new Map<number, string>()

  lines.forEach(line => {
    const trimmedLine = line.trim()
    // Skip empty lines or lines that start with a semicolon after trimming
    if (trimmedLine === "" || trimmedLine.startsWith(";")) {
      return
    }
    // First see if we have a line that is "SYMBOL equ $ADDRESS"
    // Accept spaces or tabs between the parts.
    // Watch out for optional ; comments at the end of the line.
    const equMatch = trimmedLine.match(/^([A-Za-z_][A-Za-z0-9_]*)\s+equ\s+\$([0-9A-Fa-f]{1,4})\b/i)
    if (equMatch) {
      const symbol = equMatch[1]
      const address = parseInt(equMatch[2], 16)
      symbolMap.set(address, symbol)
    } else {
      // Now look for lines that start with an address followed by a symbol.
      const parts = trimmedLine.split(/\s+/)
      if (parts.length >= 2) {
        const address = parseInt(parts[0], 16)
        const symbol = parts[1]
        symbolMap.set(address, symbol)
      }
    }
  })

  return symbolMap
}

const fetchAndProcessSymbolFile = async (url: string): Promise<Map<number, string>> => {
  const response = await fetch(url)
  const text = await response.text()
  return processSymbolData(text)
}

const loadAndProcessSymbolFile = async (file: File) => {
  const fileread = new FileReader()
  fileread.onload = function (ev) {
    if (ev.target) {
      const result = processSymbolData(ev.target.result as string)
      if (result.size === 0) {
        console.error("No symbols found in file")
      }
      userSymbolTable = result
    } else {
      console.error("File read error")
    }
  }
  fileread.onerror = function () {
    console.error("File read error")
  }
  fileread.readAsText(file)
}


export const loadUserSymbolTable = (file: File) => {
  userSymbolTable = new Map<number, string>()
  loadAndProcessSymbolFile(file)
}
// KNOWN GOOD POINT


export const getSymbolTables = (machineName: string) => {

  if (machineName_cached !== machineName) {
    machineSymbolTable = new Map<number, string>()
    machineName_cached = machineName
  }

  if (machineSymbolTable.size === 0 && !fetchingTable) {
    fetchingTable = true
    const url = "./symbol_tables/" + machineName.toLowerCase() + ".sym"
    fetchAndProcessSymbolFile(url).then(symbolMap => {
      machineSymbolTable = symbolMap
      fetchingTable = false
    }).catch(error => {
      console.error("Error fetching or processing the symbol file:", error)
    })
  }

  return [machineSymbolTable, userSymbolTable]
}

export const isHardDriveImage = (filename: string, fileSize: number) => {
  const f = filename.toLowerCase()
  if (/\.(?:woz|dsk|do)(?:$|[^a-z0-9])/.test(f)) {
    return false
  }
  if (/\.(?:hdv|2mg|2meg)(?:$|[^a-z0-9])/.test(f)) {
    return true
  }
  if (/\.(?:po|img)(?:$|[^a-z0-9])/.test(f)) {
    // 5.25" ProDOS floppy disk is 140KB (143360 bytes).
    // Converted WOZ2 floppy images from 140KB disks are ~234KB - 236KB (35-40 tracks).
    // Anything larger (e.g. 800KB 3.5" disk, 2MB, 16MB, 32MB hard drive images)
    // is treated as a Slot 7 SmartPort hard drive image.
    if (fileSize >= 230000 && fileSize <= 275000) {
      return false
    }
    return fileSize > 143360
  }
  return false
}

export const getDefaultDiskDriveIndex = (
  filename: string,
  fileSize: number,
) => isHardDriveImage(filename, fileSize) ? 0 : 2

export const constructAudio = (mp3track: string) => {
  const audioDevice: AudioDevice = {
    context: new AudioContext(),
    element: new Audio(mp3track),
    timeout: 0
  }
  audioDevice.element.volume = 0.5
  const node = audioDevice.context.createMediaElementSource(audioDevice.element)
  node.connect(audioDevice.context.destination)
  return audioDevice
}

export const playAudio = (audioDevice: AudioDevice, timeout: number) => {
  if (audioDevice.context.state === "suspended") {
    audioDevice.context.resume()
  }
  const playPromise = audioDevice.element.play()
  if (playPromise) {
    playPromise.then(() => {
      window.clearTimeout(audioDevice.timeout)
      audioDevice.timeout = window.setTimeout(() => {
        if (audioDevice.context.state !== "closed") {
          audioDevice.context.suspend()
        }
      }, timeout)
    }).catch(() => {
//      console.log(error)
    })
  }
}

export const getCleanDiskFilename = (rawName: string): string => {
  if (!rawName) return ""
  let decoded = rawName
  try {
    decoded = decodeURIComponent(rawName)
  } catch {
    // keep raw
  }
  decoded = decoded.replace(/%2f/gi, "/")
  const segments = decoded.split(/[\\/]/)
  return segments.pop()?.trim() || rawName.trim()
}

export const separateUrlAndComment = (entry: string): { url: string; comment?: string } => {
  const pipeIndex = entry.indexOf("|")
  if (pipeIndex >= 0) {
    const comment = entry.substring(pipeIndex + 1).trim()
    return {
      url: entry.substring(0, pipeIndex).trim(),
      comment: comment.length > 0 ? comment : undefined,
    }
  }
  return { url: entry.trim() }
}

export const getPrimaryDiskUrl = (item?: Partial<DiskCollectionItem> | null): string => {
  if (!item) return ""
  const source = item.disks || item.diskUrl
  if (!source) return ""
  let candidate = ""
  if (typeof source === "string") {
    candidate = source
  } else if (Array.isArray(source)) {
    candidate = source[0] || ""
  } else if (typeof source === "object") {
    const raw = source.s6d1 || source.s7d1 || source.d1 || source.hd1 || source.s6d2 || source.s7d2 || ""
    if (Array.isArray(raw)) candidate = raw[0] || ""
    else candidate = raw || ""
  }
  return separateUrlAndComment(candidate).url
}

export const resolveDiskSwapperUrls = (rawUrls: string[]): string[] => {
  if (rawUrls.length === 0) return []
  const firstParsed = separateUrlAndComment(rawUrls[0])
  const firstUrl = firstParsed.url
  const lastSlash = firstUrl.lastIndexOf("/")
  const lastEncodedSlash = firstUrl.toLowerCase().lastIndexOf("%2f")
  let basePrefix = ""
  if (lastEncodedSlash > lastSlash) {
    basePrefix = firstUrl.substring(0, lastEncodedSlash + 3)
  } else if (lastSlash >= 0) {
    basePrefix = firstUrl.substring(0, lastSlash + 1)
  }

  return rawUrls.map((rawUrl, idx) => {
    const { url, comment } = separateUrlAndComment(rawUrl)
    let resolvedUrl = url
    if (idx > 0 && !/^(https?:|\/|file:)/i.test(url) && basePrefix) {
      const encodedFilename = url.includes("%") ? url : encodeURIComponent(url)
      resolvedUrl = `${basePrefix}${encodedFilename}`
    }
    return comment ? `${resolvedUrl}|${comment}` : resolvedUrl
  })
}

export const parseDiskSwapperParam = (paramValue: string): string[] => {
  const trimmed = paramValue.trim()
  if (!trimmed) return []
  let content = trimmed
  if (content.toLowerCase().startsWith("%7b") && content.toLowerCase().endsWith("%7d")) {
    content = decodeURIComponent(content)
  }
  if (content.startsWith("{") && content.endsWith("}")) {
    content = content.slice(1, -1).trim()
    const parts = content.split(",").map(p => p.trim()).filter(Boolean)
    return resolveDiskSwapperUrls(parts)
  }
  return [trimmed]
}
